// Плашка уведомления.
// Source: docs/specs/design-revision/a-notifications.md §2.1-2.4.
//
// Что чинится по сравнению с исходной shadcn-версией (все четыре причины
// жалобы PM были независимыми):
//   1. Крестик имел opacity-0 и появлялся по group-hover. На тач-устройстве
//      hover не наступает никогда: кнопка была, нажималась, но оставалась
//      прозрачной. Теперь виден всегда, площадь нажатия 44×44.
//   2. swipeDirection у Radix по умолчанию "right", а плашка прилетала сверху.
//      Человек смахивал вверх — ничего. Теперь снизу и закрывается вниз.
//   3. Вариантов было два, default и destructive. Из 59 вызовов успех
//      рендерился белым прямоугольником, неотличимым от карточек контента.
//      Теперь четыре варианта, у каждого полоска слева и иконка.
//   4. Viewport стоял сверху и садился на ScreenHeader вместе с колокольчиком
//      непрочитанных. Теперь снизу, над нижней панелью (--hl-bottom-bar).

import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { ToastVariant } from '@/hooks/use-toast';

const ToastProvider = ToastPrimitives.Provider;

/**
 * Снизу на всех ширинах, шириной как MobileFrame (max-w-md), с отступом под
 * нижнюю панель экрана. Прежний вариант `top-0 … sm:bottom-0` на 375px никогда
 * не доходил до `sm:` и всегда оставался сверху.
 */
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'pointer-events-none fixed inset-x-0 bottom-0 z-toast mx-auto flex max-w-md flex-col gap-2 p-4',
      'pb-[calc(1rem_+_env(safe-area-inset-bottom)_+_var(--hl-bottom-bar,0px))]',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  [
    'group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden',
    'rounded-surface border bg-surface p-3.5 text-foreground shadow-overlay',
    // touch-action: свайпом управляем сами, браузеру скроллить нечего.
    'touch-none select-none',
    'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-full',
    'data-[state=closed]:animate-out data-[state=closed]:fade-out-80',
    'data-[state=closed]:slide-out-to-bottom-full',
    'motion-reduce:animate-none motion-reduce:transition-none',
  ],
  {
    variants: {
      variant: {
        success: 'border-border before:bg-primary',
        warning: 'border-border before:bg-attention',
        destructive: 'border-border before:bg-bad',
        info: 'border-border before:bg-gray-400',
      },
    },
    defaultVariants: { variant: 'info' },
  },
);

/** Смысл несёт не только цвет полоски, но и форма значка — WCAG 1.4.1. */
const ICON_BY_VARIANT: Record<ToastVariant, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  warning: TriangleAlert,
  destructive: AlertCircle,
  info: Info,
};

const ICON_TONE_BY_VARIANT: Record<ToastVariant, string> = {
  success: 'bg-primary',
  warning: 'bg-attention',
  destructive: 'bg-bad',
  info: 'bg-gray-400',
};

/** За сколько пикселей движения плашка считается смахнутой. */
const SWIPE_THRESHOLD = 56;

/**
 * Свайп вниз, влево и вправо.
 *
 * Своя реализация, а не встроенная в Radix: `swipeDirection` у него ровно один,
 * а нужны три. Встроенный свайп отключён высоким `swipeThreshold` на провайдере,
 * иначе два обработчика писали бы в один `transform`.
 */
function useSwipeDismiss(onDismiss: () => void) {
  const node = React.useRef<HTMLLIElement | null>(null);
  const start = React.useRef<{ x: number; y: number } | null>(null);

  const reset = (el: HTMLLIElement, animate: boolean) => {
    el.style.transition = animate ? 'transform .18s ease-out' : '';
    el.style.transform = '';
  };

  const onPointerDown = (e: React.PointerEvent<HTMLLIElement>) => {
    // Нажатие по кнопке внутри плашки — это нажатие, а не свайп.
    if ((e.target as HTMLElement).closest('button')) return;
    node.current = e.currentTarget;
    start.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.currentTarget.style.transition = 'none';
  };

  const onPointerMove = (e: React.PointerEvent<HTMLLIElement>) => {
    const from = start.current;
    const el = node.current;
    if (!from || !el) return;
    const dx = e.clientX - from.x;
    const dy = Math.max(0, e.clientY - from.y); // вверх не тащим: там контент
    el.style.transform =
      Math.abs(dx) > dy ? `translateX(${dx}px)` : `translateY(${dy}px)`;
  };

  const finish = (e: React.PointerEvent<HTMLLIElement>) => {
    const from = start.current;
    const el = node.current;
    start.current = null;
    node.current = null;
    if (!from || !el) return;

    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;
    const gone = Math.abs(dx) >= SWIPE_THRESHOLD || dy >= SWIPE_THRESHOLD;
    if (!gone) {
      reset(el, true);
      return;
    }

    // Уводим в ту сторону, куда тянули, и только потом гасим — чтобы плашка
    // уехала, а не мигнула.
    el.style.transition = 'transform .18s ease-out, opacity .18s ease-out';
    el.style.transform =
      Math.abs(dx) > dy ? `translateX(${dx > 0 ? 120 : -120}%)` : 'translateY(120%)';
    el.style.opacity = '0';
    window.setTimeout(onDismiss, 150);
  };

  return { onPointerDown, onPointerMove, onPointerUp: finish, onPointerCancel: finish };
}

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> & { onSwipeDismiss?: () => void }
>(({ className, variant, onSwipeDismiss, ...props }, ref) => {
  const swipe = useSwipeDismiss(onSwipeDismiss ?? (() => {}));
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(
        toastVariants({ variant }),
        // Полоска слева — 3px, цвет из варианта. Заливать всю плашку красным
        // в health-приложении тревожнее, чем нужно.
        'before:absolute before:inset-y-2.5 before:left-0 before:w-[3px] before:rounded-full before:content-[""]',
        className,
      )}
      {...(onSwipeDismiss ? swipe : {})}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

/**
 * Полоска обратного отсчёта: показывает, сколько плашке осталось.
 * У незакрываемых её нет — там отсчитывать нечего.
 */
function ToastProgress({ duration, tone }: { duration: number; tone: ToastVariant }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-3.5 bottom-1.5 h-[2px] overflow-hidden rounded-full bg-muted"
    >
      <span
        className={cn('block h-full origin-left rounded-full opacity-50', ICON_TONE_BY_VARIANT[tone])}
        style={{ animation: `hl-toast-life ${duration}ms linear forwards` }}
      />
    </span>
  );
}

/** Значок варианта. Обязателен: это нецветовой носитель смысла. */
function ToastIcon({ variant }: { variant: ToastVariant }) {
  const Icon = ICON_BY_VARIANT[variant];
  return (
    <span
      aria-hidden
      className={cn(
        'mt-0.5 flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-control text-white',
        ICON_TONE_BY_VARIANT[variant],
      )}
    >
      <Icon className="h-[15px] w-[15px]" />
    </span>
  );
}

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'mt-2 inline-flex min-h-[34px] shrink-0 items-center gap-1.5 rounded-control border border-current',
      'bg-transparent px-3 text-hl-secondary font-semibold',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

/**
 * Виден всегда. Прежние `opacity-0` и `group-hover:opacity-100` делали кнопку
 * недостижимой на телефоне — именно это и было «плашку нечем закрыть».
 */
const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      '-mr-2 -mt-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-control',
      'text-muted-foreground transition-colors hover:text-foreground',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('text-[13.5px] font-semibold leading-snug', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('mt-0.5 text-[12.5px] leading-snug text-muted-foreground', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;
type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastIcon,
  ToastProgress,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
